import { useCanonical } from '../../hooks/useCanonical';
import { useState } from 'react';
import { TERMS_VERSION } from './Terms';
import { Link } from 'react-router-dom';
import { publicApi } from '../../services/portalApi';
import { PRICE, PRICE_ZAR, BILLING_NOTE } from '../../constants/pricing';

interface FormData {
  estate_name: string;
  estate_address: string;
  admin_email: string;
  confirm_email: string;
  admin_name: string;
  admin_password: string;
  confirm_password: string;
}

const EMPTY: FormData = {
  estate_name: '',
  estate_address: '',
  admin_email: '',
  confirm_email: '',
  admin_name: '',
  admin_password: '',
  confirm_password: '',
};

export default function Register() {
  useCanonical('/register');
  const [form, setForm] = useState<FormData>(EMPTY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Explicit acceptance, not implied by clicking Continue. A ticked box the
  // person had to find is materially more defensible than notice text, and it
  // is what makes the stored timestamp mean something.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  function set(field: keyof FormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    // A typo here is unrecoverable once money has changed hands: the address
    // is the username, and a reset link would go to an address they don't own.
    if (form.admin_email.trim().toLowerCase() !== form.confirm_email.trim().toLowerCase()) {
      setError('Email addresses do not match.');
      return;
    }
    if (form.admin_password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    if (form.admin_password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    // Checked here as well as on the button: the button being disabled is a
    // hint, not a control, and this is the only thing standing between an
    // unticked box and a stored terms_version that nobody agreed to.
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    try {
      // Nothing is created yet — this only stashes the signup and returns a
      // PayFast form URL. The site is provisioned by the ITN after payment.
      const { claim_token, payment_url } = await publicApi.registerStart({
        site_name: form.estate_name,
        site_address: form.estate_address,
        admin_email: form.admin_email,
        admin_name: form.admin_name,
        admin_password: form.admin_password,
        terms_version: TERMS_VERSION,
      });
      // Remembered so the success page can poll provisioning status — the
      // buyer comes back from PayFast on a fresh page load.
      sessionStorage.setItem('patrolit-claim-token', claim_token);
      // Full navigation, not react-router: payment_url is a server-rendered
      // page that auto-POSTs to PayFast.
      window.location.href = payment_url;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md p-8">
        <div className="mb-6">
          <Link to="/" className="text-sm text-brand-600 hover:underline">← Back</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-3">Register your site</h1>
          <p className="text-sm text-gray-500 mt-1">{PRICE} per month per site · up to 10 patrollers · cancel anytime</p>
          {/* Before payment, not after. Discovering this on the far side of a
              R1 000 charge is a refund request and a bad first impression. */}
          <p className="text-xs text-gray-400 mt-2">
            Patrollers need an Android phone. The portal works in any browser —
            iPhone, iPad, Mac or Windows.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Site details */}
          <fieldset className="space-y-3">
            <legend className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Site details</legend>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site name</label>
              <input
                type="text"
                required
                value={form.estate_name}
                onChange={(e) => set('estate_name', e.target.value)}
                placeholder="Silverwood Estate / Oakdale Business Park"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input
                type="text"
                value={form.estate_address}
                onChange={(e) => set('estate_address', e.target.value)}
                placeholder="12 Main Road, Cape Town"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </fieldset>

          {/* Admin account */}
          <fieldset className="space-y-3 pt-2">
            <legend className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Admin account</legend>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
              <input
                type="text"
                required
                value={form.admin_name}
                onChange={(e) => set('admin_name', e.target.value)}
                placeholder="Jane Smith"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={form.admin_email}
                onChange={(e) => set('admin_email', e.target.value)}
                placeholder="jane@yoursite.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={form.confirm_email}
                onChange={(e) => set('confirm_email', e.target.value)}
                placeholder="jane@yoursite.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                required
                autoComplete="new-password"
                value={form.admin_password}
                onChange={(e) => set('admin_password', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type="password"
                required
                autoComplete="new-password"
                value={form.confirm_password}
                onChange={(e) => set('confirm_password', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </fieldset>

          <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span>
              I am authorised to act for this site, and I accept the{' '}
              <Link to="/terms" target="_blank" className="text-brand-600 underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" target="_blank" className="text-brand-600 underline">
                Privacy Policy
              </Link>.
            </span>
          </label>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className="w-full py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition-colors disabled:opacity-60"
          >
            {/* PRICE_ZAR, not PRICE: this button leads straight to PayFast, which
                will quote rand. Showing an approximate foreign figure here and a
                different number on the next screen reads as a bait and switch. */}
            {loading ? 'Redirecting to payment…' : `Continue to payment — ${PRICE_ZAR}/month`}
          </button>

          <p className="text-xs text-center text-gray-400">
            You'll be taken to PayFast to complete payment. Your site is created once the
            payment clears, and we'll email you when it's ready.{BILLING_NOTE}
          </p>
        </form>

        <p className="text-center text-xs text-gray-400 mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
